import {Suspense, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {ConnectionHandler} from 'relay-runtime';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {useCreateTicket} from '../hooks/useCreateTicket';
import {useMentionUsers} from '../hooks/useMentionUsers';
import {AssigneePicker} from './AssigneePicker';
import {TicketLabelPicker} from './TicketLabelPicker';
import type {TicketPriority} from '../types/ticket';

interface CreateTicketModalProps {
	open: boolean;
	onClose: () => void;
	onCreated?: (ticket: {id: string; slug: string}) => void;
}

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	background: rgb(0 0 0 / 0.4);
`;

const Dialog = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 28rem;
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: 12px;
	background: #ffffff;
	padding: 24px;
	${raisedSurfaceShadow}
`;

const Header = styled.div`
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Title = styled.h2`
	margin: 0;
	font-size: 18px;
	line-height: 28px;
	font-weight: 500;
	color: #0f172a;
`;

const CloseIcon = styled.svg`
	height: 20px;
	width: 20px;
`;

const ErrorBox = styled.div`
	margin-bottom: 16px;
	border-radius: 4px;
	border: 1px solid #fecaca;
	background: #fef2f2;
	padding: 12px 16px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #b91c1c;
`;

const Form = styled.form`
	> * + * {
		margin-top: 20px;
	}
`;

const FieldLabel = styled.label`
	display: block;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;

const FieldLabelSpan = styled.span`
	display: block;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;

const fieldStyles = `
	margin-top: 4px;
	width: 100%;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 8px 12px;
`;

const TextInput = styled.input`
	${fieldStyles}
	${p => p.theme.sys.font?.subheading?.['2']}
	&:focus {
		outline: 2px solid ${p => p.theme.sys.color.brand.primary['1']};
		outline-offset: 2px;
		border-color: ${p => p.theme.sys.color.brand.primary['1']};
		box-shadow: none;
	}
`;

const TextArea = styled.textarea`
	${fieldStyles}
	${p => p.theme.sys.font?.subheading?.['2']}
	&:focus {
		outline: 2px solid ${p => p.theme.sys.color.brand.primary['1']};
		outline-offset: 2px;
		border-color: ${p => p.theme.sys.color.brand.primary['1']};
		box-shadow: none;
	}
`;

const Select = styled.select`
	${fieldStyles}
	${p => p.theme.sys.font?.subheading?.['2']}
	&:focus {
		outline: 2px solid ${p => p.theme.sys.color.brand.primary['1']};
		outline-offset: 2px;
		border-color: ${p => p.theme.sys.color.brand.primary['1']};
		box-shadow: none;
	}
`;

const PickerSlot = styled.div`
	margin-top: 4px;
`;

const LabelColumn = styled.div`
	margin-top: 4px;
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const LabelChips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #475569;
`;

const LabelChip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: 9999px;
	background: #f1f5f9;
	padding: 4px 8px;
`;

const LoadingText = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
`;

const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

export function CreateTicketModal({open, onClose, onCreated}: CreateTicketModalProps) {
	const {create, loading, error: serverError} = useCreateTicket();

	const [subject, setSubject] = useState('');
	const [description, setDescription] = useState('');
	const [priority, setPriority] = useState<TicketPriority>('p2');
	const [requesterEmail, setRequesterEmail] = useState('');
	const [assigneeId, setAssigneeId] = useState<string | null>(null);
	const [labelIds, setLabelIds] = useState<string[]>([]);
	const [validationError, setValidationError] = useState('');
	const {users} = useMentionUsers();

	const subjectRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (open) subjectRef.current?.focus();
	}, [open]);

	if (!open) return null;

	function resetForm() {
		setSubject('');
		setDescription('');
		setPriority('p2');
		setRequesterEmail('');
		setAssigneeId(null);
		setLabelIds([]);
		setValidationError('');
	}

	function handleClose() {
		resetForm();
		onClose();
	}

	// Two entry points reach this: the form's onSubmit (Enter inside a field — HTML
	// implicit submission) and the design-system footer Button. Keep them separate:
	// the Button is React Aria driven and calls onPress with a synthetic PressEvent
	// that has NO preventDefault, so only the form path may touch the event.
	function handleFormSubmit(e: React.FormEvent) {
		e.preventDefault();
		void handleSubmit();
	}

	async function handleSubmit() {
		setValidationError('');

		if (!subject.trim()) {
			setValidationError('Subject is required');
			return;
		}

		try {
			const connectionId = ConnectionHandler.getConnectionID('client:root', 'TicketList_tickets');
			const ticket = await create(
				{
					subject: subject.trim(),
					description: description.trim() || undefined,
					priority,
					requesterEmail: requesterEmail.trim() || undefined,
					assigneeId: assigneeId ?? undefined,
					labels: labelIds.length > 0 ? labelIds : undefined,
				},
				[connectionId]
			);
			resetForm();
			onClose();
			onCreated?.(ticket);
		} catch {
			// error is already set in the hook's state and rendered below
		}
	}

	const displayError = validationError || serverError;

	return (
		<Overlay>
			<Backdrop data-testid="create-ticket-backdrop" onClick={handleClose} />
			<Dialog role="dialog" aria-labelledby="create-ticket-title">
				<Header>
					<Title id="create-ticket-title">New ticket</Title>
					<LabelledButton emphasis={EMPHASIS.LOW} label="Close" onPress={handleClose}>
						<CloseIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
						</CloseIcon>
					</LabelledButton>
				</Header>

				{displayError && <ErrorBox role="alert">{displayError}</ErrorBox>}

				<Form onSubmit={handleFormSubmit} noValidate>
					<div>
						<FieldLabel htmlFor="subject">Subject</FieldLabel>
						<TextInput
							id="subject"
							ref={subjectRef}
							type="text"
							value={subject}
							onChange={e => setSubject(e.target.value)}
							placeholder="Brief summary of the issue"
						/>
					</div>

					<div>
						<FieldLabel htmlFor="description">Description</FieldLabel>
						<TextArea
							id="description"
							value={description}
							onChange={e => setDescription(e.target.value)}
							placeholder="Detailed description or initial message"
							rows={4}
						/>
					</div>

					<div>
						<FieldLabel htmlFor="priority">Priority</FieldLabel>
						<Select id="priority" value={priority} onChange={e => setPriority(e.target.value as TicketPriority)}>
							<option value="p3">Low</option>
							<option value="p2">Normal</option>
							<option value="p1">High</option>
							<option value="p0">Urgent</option>
						</Select>
					</div>

					<div>
						<FieldLabel htmlFor="requesterEmail">Requester email</FieldLabel>
						<TextInput
							id="requesterEmail"
							type="email"
							value={requesterEmail}
							onChange={e => setRequesterEmail(e.target.value)}
							placeholder="customer@example.com (defaults to you)"
						/>
					</div>

					<div>
						<FieldLabelSpan>Assignee</FieldLabelSpan>
						<PickerSlot>
							<AssigneePicker
								assigneeId={assigneeId}
								users={users}
								disabled={loading}
								onChange={userId => setAssigneeId(userId)}
							/>
						</PickerSlot>
					</div>

					<div>
						<FieldLabelSpan>Labels</FieldLabelSpan>
						<LabelColumn>
							{labelIds.length > 0 && (
								<LabelChips>
									{labelIds.map(id => (
										<LabelChip key={id}>
											{id}
											<LabelledButton
												emphasis={EMPHASIS.LOW}
												label={`Remove label ${id}`}
												onPress={() => setLabelIds(prev => prev.filter(x => x !== id))}
											>
												×
											</LabelledButton>
										</LabelChip>
									))}
								</LabelChips>
							)}
							<Suspense fallback={<LoadingText>Loading…</LoadingText>}>
								<TicketLabelPicker
									ticketId=""
									currentLabelIds={labelIds}
									disabled={loading}
									onAdd={id => setLabelIds(prev => (prev.includes(id) ? prev : [...prev, id]))}
								/>
							</Suspense>
						</LabelColumn>
					</div>

					<Actions>
						<Button emphasis={EMPHASIS.LOW} disabled={loading} onPress={handleClose}>
							Cancel
						</Button>
						<Button emphasis={EMPHASIS.HIGH} disabled={loading} onPress={handleSubmit}>
							{loading ? 'Creating…' : 'Create Ticket'}
						</Button>
					</Actions>
				</Form>
			</Dialog>
		</Overlay>
	);
}
